// INTELIMOVE-SAAS-002 (IM-ADR-020): regras puras da administração operacional — diagnóstico de organizações
// duplicadas, alertas do cockpit e formato dos códigos de uso único. Sem banco, sem rede: testáveis isoladamente.
import { z } from 'zod';
import type { PlatformAlert } from './platform.js';

// ── Diagnóstico de duplicidades ──────────────────────────────────────────────────────────────────────────────────
export interface DuplicateCandidateRow {
  id: string;
  slug: string;
  name: string;
  tradeName: string | null;
  legalName: string | null;
  purpose: 'REAL' | 'TECNICO' | 'DEMONSTRACAO';
  operationalStatus: 'ACTIVE' | 'SUSPENDED';
  primaryDomain: string | null;
  taxId: string | null;
  members: number;
  owners: number;
  createdAt: string;
  lastActivityAt: string | null;
  /** Domínios de e-mail de proprietários/administradores (só o domínio). */
  staffDomains: string[];
}

export type DuplicateConfidence = 'ALTA' | 'MEDIA' | 'BAIXA';
export type DuplicateCriterion = 'CNPJ' | 'DOMINIO' | 'DOMINIO_RAIZ' | 'DOMINIO_EQUIPE' | 'NOME' | 'NOME_SEMELHANTE';

export const DUPLICATE_CRITERION_LABEL: Record<DuplicateCriterion, string> = {
  CNPJ: 'Mesmo CNPJ',
  DOMINIO: 'Mesmo domínio principal',
  DOMINIO_RAIZ: 'Mesmo domínio com outra terminação (.com.br/.net…)',
  DOMINIO_EQUIPE: 'Mesmo domínio de e-mail corporativo dos responsáveis',
  NOME: 'Mesmo nome normalizado',
  NOME_SEMELHANTE: 'Nome contido no outro',
};

const CRITERION_WEIGHT: Record<DuplicateCriterion, DuplicateConfidence> = {
  CNPJ: 'ALTA', DOMINIO: 'ALTA', DOMINIO_RAIZ: 'MEDIA', DOMINIO_EQUIPE: 'MEDIA', NOME: 'MEDIA', NOME_SEMELHANTE: 'BAIXA',
};

export interface DuplicateGroup {
  key: string;
  confidence: DuplicateConfidence;
  criteria: DuplicateCriterion[];
  organizations: Array<DuplicateCandidateRow & { suggestedKeep: boolean }>;
  recommendation: string;
}

/** Provedores de e-mail pessoal: o domínio não identifica a empresa. */
const FREE_MAIL = new Set(['gmail.com', 'hotmail.com', 'outlook.com', 'live.com', 'yahoo.com', 'yahoo.com.br', 'icloud.com', 'uol.com.br', 'bol.com.br', 'terra.com.br', 'protonmail.com', 'proton.me', 'msn.com', 'ig.com.br']);
/** Domínios de teste/automação não indicam empresa real. */
const TEST_DOMAIN = /(^|\.)(test|example|invalid|localhost|local|internal)$|\.test\./;
const LEGAL_SUFFIX = /\b(ltda|me|epp|eireli|s ?a|sa|s\/a|inc|llc|ltd|cia|companhia|grupo|holding)\b/g;

export function normalizeOrgName(name: string | null | undefined): string {
  return (name ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ').replace(LEGAL_SUFFIX, ' ').replace(/\s+/g, ' ').trim();
}

export function normalizeDomain(d: string | null | undefined): string | null {
  const v = (d ?? '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
  return v && v.includes('.') && !TEST_DOMAIN.test(v) ? v : null;
}

/** Raiz do domínio sem a terminação pública: bugkillers.com.br e bugkillers.net → "bugkillers". */
export function domainRoot(d: string | null): string | null {
  if (!d) return null;
  const parts = d.split('.');
  const tail2 = parts.slice(-2).join('.');
  const cut = /^(com|net|org|gov|edu|ind|art|eng|adv)\.[a-z]{2}$/.test(tail2) ? 3 : 2;
  const root = parts.length >= cut ? parts[parts.length - cut]! : parts[0]!;
  return root.length >= 3 ? root : null;
}

const onlyDigits = (v: string | null) => (v ?? '').replace(/\D/g, '');

function edges(a: DuplicateCandidateRow, b: DuplicateCandidateRow): DuplicateCriterion[] {
  const out: DuplicateCriterion[] = [];
  const ta = onlyDigits(a.taxId); const tb = onlyDigits(b.taxId);
  if (ta.length === 14 && ta === tb) out.push('CNPJ');
  const da = normalizeDomain(a.primaryDomain); const db = normalizeDomain(b.primaryDomain);
  if (da && da === db) out.push('DOMINIO');
  else if (da && db && domainRoot(da) && domainRoot(da) === domainRoot(db)) out.push('DOMINIO_RAIZ');
  const sa = new Set(a.staffDomains.map(normalizeDomain).filter((x): x is string => !!x && !FREE_MAIL.has(x)));
  const sb = b.staffDomains.map(normalizeDomain).filter((x): x is string => !!x && !FREE_MAIL.has(x));
  if (sb.some((x) => sa.has(x))) out.push('DOMINIO_EQUIPE');
  const na = normalizeOrgName(a.tradeName ?? a.name); const nb = normalizeOrgName(b.tradeName ?? b.name);
  if (na && na === nb) out.push('NOME');
  else if (na.length >= 4 && nb.length >= 4 && (na.includes(nb) || nb.includes(na))) out.push('NOME_SEMELHANTE');
  return out;
}

const RANK: Record<DuplicateConfidence, number> = { ALTA: 3, MEDIA: 2, BAIXA: 1 };

/**
 * Confiança do par: ALTA com CNPJ ou domínio iguais, ou com dois critérios médios independentes (ex.: nome + domínio
 * da equipe); MÉDIA com um critério médio; BAIXA só com nome semelhante. Domínio da equipe sozinho não liga o par.
 */
export function pairConfidence(criteria: DuplicateCriterion[]): DuplicateConfidence | null {
  // Domínio da equipe só reforça: a mesma consultoria (ou a equipe técnica) pode ser responsável por empresas diferentes.
  if (!criteria.some((c) => c !== 'DOMINIO_EQUIPE')) return null;
  if (criteria.some((c) => CRITERION_WEIGHT[c] === 'ALTA')) return 'ALTA';
  const medium = criteria.filter((c) => CRITERION_WEIGHT[c] === 'MEDIA').length;
  if (medium >= 2) return 'ALTA';
  if (medium === 1) return 'MEDIA';
  return 'BAIXA';
}

/**
 * Agrupa organizações que provavelmente são a mesma empresa (união dos pares com algum critério). Só diagnóstico: não
 * funde, não move, não apaga. A organização sugerida para manter é a com mais membros, depois a de atividade mais
 * recente, depois a mais antiga — sempre sujeita à decisão do dono e a backup.
 */
export function groupDuplicateCandidates(rows: DuplicateCandidateRow[]): DuplicateGroup[] {
  const parent = rows.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  const pairs: Array<{ i: number; criteria: DuplicateCriterion[]; confidence: DuplicateConfidence }> = [];
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const criteria = edges(rows[i]!, rows[j]!);
      const confidence = pairConfidence(criteria);
      if (!confidence) continue;
      pairs.push({ i, criteria, confidence });
      parent[find(j)] = find(i);
    }
  }
  const pairCriteria = new Map<number, Set<DuplicateCriterion>>();
  const pairConf = new Map<number, DuplicateConfidence>();
  for (const p of pairs) {
    const root = find(p.i);
    pairCriteria.set(root, new Set([...(pairCriteria.get(root) ?? []), ...p.criteria]));
    const prev = pairConf.get(root);
    if (!prev || RANK[p.confidence] > RANK[prev]) pairConf.set(root, p.confidence);
  }
  const members = new Map<number, number[]>();
  rows.forEach((_, i) => { const r = find(i); members.set(r, [...(members.get(r) ?? []), i]); });
  const groups: DuplicateGroup[] = [];
  for (const [root, idx] of members) {
    if (idx.length < 2) continue;
    const orgs = idx.map((i) => rows[i]!);
    const keep = [...orgs].sort((a, b) => b.members - a.members
      || (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? '')
      || a.createdAt.localeCompare(b.createdAt))[0]!;
    const confidence = pairConf.get(root) ?? 'BAIXA';
    const criteria = [...(pairCriteria.get(root) ?? [])].sort((a, b) => RANK[CRITERION_WEIGHT[b]] - RANK[CRITERION_WEIGHT[a]]);
    const mixed = new Set(orgs.map((o) => o.purpose)).size > 1;
    groups.push({
      key: orgs.map((o) => o.id).sort()[0]!,
      confidence,
      criteria,
      organizations: orgs.sort((a, b) => a.createdAt.localeCompare(b.createdAt)).map((o) => ({ ...o, suggestedKeep: o.id === keep.id })),
      recommendation: confidence === 'BAIXA'
        ? 'Semelhança fraca (só o nome). Confirme com os responsáveis antes de qualquer ação.'
        : `Provável mesma empresa (${orgs.length} organizações). Sugestão para manter: ${keep.name} (${keep.slug}), com mais membros ou atividade. `
          + 'Consolidação só com autorização específica do dono e backup prévio (scripts/org-consolidation.ts: prévia → --apply); nada é movido nem apagado automaticamente.'
          + (mixed ? ' Atenção: o grupo mistura finalidades (real, técnica ou demonstração).' : ''),
    });
  }
  return groups.sort((a, b) => RANK[b.confidence] - RANK[a.confidence] || b.organizations.length - a.organizations.length);
}

// ── Alertas do cockpit ───────────────────────────────────────────────────────────────────────────────────────────
export interface AlertInput {
  schemaOk: boolean;
  workersLive: number;
  queue: { queued: number; running: number; dead: number };
  held: { jobs: number; organizations: number };
  organizationsSuspended: number;
  admins: number;
  adminsWithMfa: number;
  withoutMfa: number;
  withoutRecovery: number;
  platformByRecovery: number;
  duplicateGroups: number;
}

/** Alertas operacionais a partir de contagens reais (nunca dado de tenant). Ordenados por gravidade. */
export function platformAlerts(i: AlertInput, base = '/admin/plataforma'): PlatformAlert[] {
  const a: PlatformAlert[] = [];
  if (!i.schemaOk) a.push({ code: 'schema', level: 'critical', message: 'Versão do banco diferente da esperada pela aplicação.', to: `${base}/estado` });
  if (i.workersLive === 0) a.push({ code: 'worker', level: 'critical', message: 'Nenhum worker ativo: coletas, análises e agendas estão paradas.', to: `${base}/estado` });
  if (i.adminsWithMfa === 0) a.push({ code: 'no_admin', level: 'critical', message: 'Nenhum administrador da plataforma com segundo fator confirmado.', to: `${base}/administradores` });
  else if (i.admins === 1) a.push({ code: 'single_admin', level: 'warning', message: 'Só um administrador da plataforma: conceda um segundo para não depender de uma pessoa.', to: `${base}/administradores` });
  if (i.queue.dead > 0) a.push({ code: 'dead_jobs', level: 'warning', message: `${i.queue.dead} trabalho(s) em falha definitiva na fila.`, to: `${base}/estado` });
  if (i.withoutMfa > 0) a.push({ code: 'mfa_pending', level: 'warning', message: `${i.withoutMfa} papel(is) da plataforma sem segundo fator confirmado.`, to: `${base}/administradores` });
  if (i.withoutRecovery > 0) a.push({ code: 'recovery_missing', level: 'warning', message: `${i.withoutRecovery} pessoa(s) sem códigos de recuperação disponíveis.`, to: `${base}/administradores` });
  if (i.platformByRecovery > 0) a.push({ code: 'recovery_session', level: 'warning', message: `${i.platformByRecovery} sessão(ões) administrativa(s) aberta(s) com código de recuperação.`, to: `${base}/sessoes` });
  if (i.organizationsSuspended > 0) a.push({ code: 'suspended', level: 'info', message: `${i.organizationsSuspended} organização(ões) suspensa(s)${i.held.jobs ? ` · ${i.held.jobs} trabalho(s) retido(s)` : ''}.`, to: `${base}/organizacoes?situacao=SUSPENDED` });
  if (i.duplicateGroups > 0) a.push({ code: 'duplicates', level: 'info', message: `${i.duplicateGroups} grupo(s) de organizações possivelmente duplicadas (confiança alta).`, to: `${base}/organizacoes/duplicidades` });
  return a;
}

// ── Códigos de uso único ─────────────────────────────────────────────────────────────────────────────────────────
/** Normaliza código digitado (sem hífen/espaço, maiúsculas) antes de calcular o HMAC. */
export const normalizeOneTimeCode = (v: string): string => v.toUpperCase().replace(/[^A-Z2-7]/g, '');
/** Agrupa para leitura: ABCDE-FGHIJ / ABCD-EFGH-… */
export const formatOneTimeCode = (v: string, group = 5): string => (v.match(new RegExp(`.{1,${group}}`, 'g')) ?? []).join('-');

// ── Ativação da identidade administrativa dedicada (INTELIMOVE-STD-001) ─────────────────────────────────────────
export const PlatformActivationTokenRequest = z.object({ token: z.string().min(32).max(128) }).strict();
export const PlatformActivationCompleteRequest = PlatformActivationTokenRequest.extend({
  password: z.string().min(10).max(512),
}).strict();
export interface PlatformActivationPreviewDto {
  name: string;
  email: string;
  kind: 'ACTIVATION' | 'RECOVERY';
  expiresAt: string;
}
